<template>
  <div>
    <HeadSection
      permissionOfSection="custom-pages"
      :breadcrumbs="breadcrumbs"
      :title="name"
      :uuid="currentUUID"
    >
      <template #activeSwitch>
        <div class="actions">
          <a-switch :checked="isActive" @change="handleToggleActive" />
          <a-button type="link" class="p-0" @click="handleRedirectToSettings">
            <a-icon type="setting" style="font-size: 20px" />
          </a-button>
        </div>
      </template>
      <template #actions>
        <div
          class="d-flex"
          style="gap: 16px; width: 100%; justify-content: end"
        >
          <a-button
            type="secondary"
            @click.prevent="handleSave"
            :disabled="!canSave"
          >
            Save
          </a-button>
          <a-tooltip
            v-if="publishPartialLangs"
            placement="left"
            :title="`Custom page must have a header and at least one category in a partially filled language:
            ${publishPartialLangs.join(', ')}`"
          >
            <a-button type="primary" disabled>Publish</a-button>
          </a-tooltip>
          <a-button
            v-else
            type="primary"
            :disabled="!isPublishable"
            @click.prevent="handlePublish"
          >
            Publish
          </a-button>
        </div>
      </template>
    </HeadSection>
    <div class="constructor-container">
      <MainContainer
        :loading="loading"
        :isBlockStyle="true"
        permissionOfSection="custom-pages"
        class="content"
      >
        <a-tabs v-model="activeTabID" type="card" class="constructor__tabs">
          <a-tab-pane v-for="tab in tabs" :key="tab.id" :tab="tab.name">
            <template>
              <component :is="tab.component" />
            </template>
          </a-tab-pane>
        </a-tabs>
      </MainContainer>
      <MainContainer
        v-if="!loading && activeTabID === 'settings'"
        :loading="loading"
        :isBlockStyle="true"
        class="phone-preview"
      >
        <PhonePreview />
      </MainContainer>
    </div>
  </div>
</template>
<script>
import HeadSection from '@/components/main/headSection'
import MainContainer from '@/components/main/mainContainer'

import Content from '@/views/apps/custom-pages/constructor/content/index.vue'
import Filter from '@/views/apps/custom-pages/constructor/filter/index.vue'
import Settings from '@/views/apps/custom-pages/constructor/settings/index.vue'
import PhonePreview from '@/views/apps/custom-pages/components/phone/PhonePreview.vue'

import apiClient from '@/services/axios'
import { isFormColorsMatchTheme } from '@/services/utils/custom-pages'

import { mapState, mapGetters, mapActions } from 'vuex'

export default {
  components: {
    HeadSection,
    MainContainer,
    PhonePreview,
  },
  data() {
    return {
      validTabs: ['content', 'filter', 'settings'],
      activeTabID: this.$route.query.tab || 'content',
      tabs: [
        {
          id: 'content',
          name: 'Content',
          component: Content,
        },
        {
          id: 'filter',
          name: 'Filter',
          component: Filter,
        },
        {
          id: 'settings',
          name: 'Settings',
          component: Settings,
        },
      ],
    }
  },
  computed: {
    ...mapState(['user']),
    ...mapGetters([
      'fetchedData',
      'fetchedDataLoading',
      'isActive',
      'isPublished',
      'isPublishable',
      'isPageFilled',
    ]),
    ...mapGetters('content', ['blocks', 'header']),
    ...mapGetters({
      settingsForm: 'settings/form',
      customThemes: 'settings/customThemes',
    }),
    currentUUID() {
      return this.$route.params.id ?? ''
    },
    userLoading() {
      return this.$store.state?.user?.loading || false
    },
    loading() {
      return this.userLoading || this.fetchedDataLoading || !this.isPageFilled
    },
    breadcrumbs() {
      return [
        { name: 'Сustom pages', link: '/custom-pages' },
        {
          name: 'Constructor',
          link: undefined,
        },
      ]
    },
    name() {
      return this.fetchedData?.name
    },
    publishPartialLangs() {
      const langs = this.$store.getters['langs'].langData || []

      const partiallyFilledLangs = []

      langs.forEach((lang) => {
        const langKey = lang.name

        const headerData = this.header?.[langKey]
        const blocksData = this.blocks?.[langKey]

        const hasHeaderImage = !!(
          headerData?.image?.url || typeof headerData?.image?.id === 'number'
        )
        const hasCategory =
          Array.isArray(blocksData) &&
          blocksData.some((b) => b.type === 'category')
        const hasOtherBlocks =
          Array.isArray(blocksData) &&
          blocksData.some((b) => b.type !== 'category')

        if (!hasHeaderImage && !hasCategory && !hasOtherBlocks) return

        if (!(hasHeaderImage && hasCategory)) {
          partiallyFilledLangs.push(lang.desc ?? lang.name)
        }
      })

      return partiallyFilledLangs.length ? partiallyFilledLangs : null
    },
    canSave() {
      const langs = this.$store.getters['langs'].langData || []

      return langs.every((lang) => {
        const langKey = lang.name
        const form = this.settingsForm[langKey]

        const selectedTheme = this.customThemes.find(
          (t) => t.id === form.theme,
        )

        if (!selectedTheme) {
          return false
        }

        if (selectedTheme.name === 'System' || selectedTheme.id === 1) {
          return true
        }

        return isFormColorsMatchTheme(form, selectedTheme)
      })
    },
  },
  methods: {
    ...mapActions([
      'createPayload',
      'fetchData',
      'fetchSports',
      'fillCustomPage',
      'getLangs',
      'setIsPublished',
      'setIsPublishable',
      'initPage',
    ]),
    ...mapActions('general', ['fetchSegments']),
    ...mapActions('settings', ['fetchThemes']),
    ...mapActions('content', {
      initHeader: 'initForm',
    }),
    ...mapActions('filter', {
      initFilter: 'initForm',
    }),
    ...mapActions('settings', {
      initSettings: 'initForm',
    }),
    handleRedirectToSettings() {
      return this.$router.push(`/custom-pages/settings/${this.currentUUID}`)
    },
    async handleToggleActive(state) {
      const url = `admin/custom-page/chance-active/${this.currentUUID}`

      try {
        const resp = await apiClient.post(url, { active: state })

        if (resp?.status !== 200) {
          throw resp
        }

        this.$notification.success({
          message: 'Custom page active status changed',
        })

        this.$store.dispatch('setIsActive', state)
      } catch (e) {
        this.$store.dispatch('setIsActive', !state)

        this.$notification.error({
          message: 'Error while toggling active state',
          description: e.message,
        })
      }
    },
    async handleSave() {
      const payload = await this.createPayload()

      const url = `/admin/custom-page/${this.currentUUID}`

      try {
        const resp = await apiClient.put(url, payload)

        if (!resp) {
          throw new Error()
        }

        if (resp.status !== 200) {
          return
        }

        this.$notification.success({
          message: 'Custom page changes saved',
        })

        this.setIsPublishable(resp.data?.data?.is_publishable ?? false)
      } catch (e) {
        this.$notification.error({
          message: e.message ?? 'Error while upading custom page',
        })
        console.error('Error upading custom page:', e)
      }
    },
    async handlePublish() {
      const url = `/admin/custom-page/chance-publication/${this.currentUUID}`

      try {
        const resp = await apiClient.post(url, {
          publish: true,
        })

        if (!resp) {
          throw new Error()
        }

        if (resp.status !== 200) {
          return
        }

        this.$notification.success({
          message: 'Custom page published',
        })

        this.$router.push('/custom-pages')
      } catch (e) {
        this.$notification.error({
          message: 'Error while publishing custom page',
          description: e.message,
        })

        console.error('Error publishing custom page:', e)
      }
    },
    fillContentPage() {
      this.fillCustomPage(this.fetchedData)
    },
    ensureValidTab(tab) {
      if (this.validTabs.includes(tab)) {
        this.activeTabID = tab
      } else {
        this.$router.replace({
          query: {
            ...this.$route.query,
            tab: 'content',
          },
        })
        this.activeTabID = 'content'
      }
    },
  },
  watch: {
    activeTabID(newTab) {
      this.$router.replace({
        query: {
          ...this.$route.query,
          tab: newTab,
        },
      })
    },
    '$route.query.tab'(newTab) {
      this.ensureValidTab(newTab)
    },
  },
  async created() {
    this.ensureValidTab(this.$route.query.tab)

    if (!this.isPageFilled) {
      this.initPage(this.currentUUID)
    }
  },
  beforeRouteLeave(to, from, next) {
    if (!to.name.startsWith('edit-custom-page')) {
      this.$store.dispatch('resetState')
    }

    next()
  },
}
</script>
<style scoped>
.constructor-container {
  display: flex;
  gap: 20px;
}

.constructor-container .content {
  flex: 1 1 auto;
}

.constructor__tabs {
  overflow: visible;
}

.constructor__tabs >>> .ant-tabs-bar {
  margin-bottom: 0;
}

.constructor__tabs >>> .ant-tabs-tab {
  margin-right: 2px !important;
}

.constructor__tabs
  >>> .ant-tabs-content
  .ant-tabs-tabpane[aria-hidden="false"] {
  border: solid #e4e9f0;
  border-width: 0 1px 1px 1px;
  border-radius: 0 4px 4px 4px;
  background: #fff;
}

.title {
  font-weight: 600;
  font-size: 20px;
  line-height: 28px;
  color: #262626;
}

.subtitle {
  margin-top: 6px;
}

.actions {
  display: flex;
  align-items: center;
  height: 30px;
  gap: 8px;
  margin-left: 16px;
}

.actions >>> .ant-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  height: auto;
}

.actions >>> .anticon {
  height: 20px;
  width: 20px;
}

.phone-preview {
  padding: 0;
  height: 100%;
  flex-shrink: 0;
}
</style>
